import React, { memo } from "react";
import { Box } from "@mui/material";
import {
  BRAND,
  GREY,
  BASE,
  BORDER,
  SUCCESS,
  PRIMARY,
} from "../../../../../utils/constants/colors";
import { fontWeight, ICON_LABELS } from "../../../../../utils/constants/UI";
import IconComponent from "../../../../../components/Icon/IconComponent";
import TypographyInline from "../../../../../components/Typography/TypographyInline";
import { Button } from "../../../../../components/Buttons";
import { sharedBillingStyles as s, FEATURE_KEY_LABELS } from "./billingUtils";

/**
 * BillingPlanCard
 *
 * Renders a single non-SDR plan card with price, credits, and feature table.
 */
const BillingPlanCard = memo(
  ({
    plan,
    isCurrent,
    isSelected,
    billingCycle,
    takenOnceFreePlan,
    buyingPlanId,
    onSelect,
    onBuy,
    hasActiveSub,
  }) => {
    const isFeatured = plan.featured && !isCurrent;
    const isLocked = plan.isFree && takenOnceFreePlan;

    const hasFeatures =
      typeof plan.features === "object" &&
      !Array.isArray(plan.features) &&
      Object.keys(plan.features || {}).length > 0;
    const fallbackFeatures = {
      icp: { credits: 10, upto: Math.round(plan.credits * 0.1).toLocaleString() },
      personalisation: { credits: 2, upto: Math.round(plan.credits * 0.5).toLocaleString() },
      reveal_details: { credits: 1, upto: plan.credits.toLocaleString() },
    };
    const rawFeatures = hasFeatures ? plan.features : fallbackFeatures;
    const isEmptyValue = (v) =>
      v === 0 || v === "0" || v == null || v === "" || v === "-";
    const ALLOWED_FEATURE_KEYS = ["icp", "personalisation", "reveal_details"];
    const featuresToRender = Object.fromEntries(
      Object.entries(rawFeatures).filter(
        ([k, v]) =>
          ALLOWED_FEATURE_KEYS.includes(k) &&
          !(isEmptyValue(v?.credits) && isEmptyValue(v?.upto)),
      ),
    );
    const cardBorder = isSelected || isCurrent
      ? `2px solid ${BRAND.PRIMARY}`
      : isFeatured
        ? `1px solid ${PRIMARY.W_200}`
        : `1px solid ${BORDER.DEFAULT}`;

    const cardShadow = isSelected || isCurrent
      ? `0 16px 28px -22px ${BRAND.PRIMARY}`
      : isFeatured
        ? `0 14px 26px -22px ${GREY.SECONDARY}`
        : `0 12px 24px -22px ${GREY.SECONDARY}`;

    const cardStyle = {
      borderRadius: "14px",
      padding: "23px 22px 22px",
      display: "flex",
      flexDirection: "column",
      gap: "14px",
      position: "relative",
      overflow: "visible",
      transition:
        "border-color 0.18s ease, box-shadow 0.18s ease, transform 0.18s ease",
      background: BASE.WHITE,
      cursor: "pointer",
      minHeight: "100%",
    };

    return (
      <Box
        onClick={onSelect}
        onKeyDown={(e) => {
          if (e.key === "Enter" || e.key === " ") {
            e.preventDefault();
            onSelect();
          }
        }}
        role="button"
        tabIndex={0}
        aria-pressed={isSelected}
        style={{
          ...cardStyle,
          border: cardBorder,
          boxShadow: cardShadow,
        }}
        sx={{
          "&:hover": {
            borderColor:
              isSelected || isCurrent || isFeatured ? BRAND.PRIMARY : BORDER.HOVER,
            boxShadow:
              isSelected || isCurrent
                ? `0 18px 30px -22px ${BRAND.PRIMARY}`
                : `0 16px 28px -22px ${GREY.SECONDARY}`,
            transform: "translateY(-2px)",
          },
          "&:focus-visible": {
            outline: `2px solid ${BRAND.PRIMARY}`,
            outlineOffset: "2px",
          },
        }}
      >
        {/* Popular badge */}
        {isFeatured && (
          <Box
            style={{
              ...s.featuredBadge,
              top: "-14px",
              padding: "6px 16px",
              border: `1px solid ${PRIMARY.W_400}`,
              boxShadow: `0 14px 24px -22px ${BRAND.PRIMARY}`,
              letterSpacing: "0.3px",
            }}
          >
            MOST POPULAR
          </Box>
        )}

        {/* Plan name + badges row */}
        <Box style={{ display: "flex", justifyContent: "space-between", alignItems: "flex-start", gap: "12px" }}>
          <Box style={{ display: "flex", gap: "12px", alignItems: "center", minWidth: 0, flex: 1 }}>
            <Box
              style={{
                width: "40px",
                height: "40px",
                borderRadius: "12px",
                background: BRAND.PRIMARY,
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                flexShrink: 0,
              }}
            >
              <IconComponent
                iconLabel={ICON_LABELS.MONETIZATION_ON}
                style={{ color: BASE.WHITE, fontSize: "20px" }}
              />
            </Box>
            <Box style={{ minWidth: 0 }}>
              <TypographyInline
                size="md"
                fontWeight={fontWeight.SEMIBOLD}
                color={GREY.PRIMARY}
                label={plan.name}
              />
              <Box style={{ marginTop: "2px" }}>
                <TypographyInline
                  size="xs"
                  color={GREY.TERTIARY}
                  label={`${billingCycle === "month" ? "Monthly" : "Yearly"} · USD`}
                />
              </Box>
            </Box>
          </Box>
          <Box style={{ display: "flex", gap: "5px", flexWrap: "wrap", justifyContent: "flex-end", flexShrink: 0 }}>
            {plan.isDashboard && (
              <span
                style={{
                  ...s.badge,
                  background: SUCCESS.W_50,
                  color: SUCCESS.W_600,
                  fontSize: "9px",
                  padding: "2px 8px",
                  height: "fit-content",
                }}
              >
                DASH
              </span>
            )}
            {plan.isCrm && (
              <span
                style={{
                  ...s.badge,
                  background: SUCCESS.W_50,
                  color: SUCCESS.W_600,
                  fontSize: "9px",
                  padding: "2px 8px",
                  height: "fit-content",
                }}
              >
                CRM
              </span>
            )}
            {isCurrent && (
              <span
                style={{
                  ...s.badge,
                  background: SUCCESS.W_500,
                  color: BASE.WHITE,
                  fontSize: "9px",
                  padding: "2px 8px",
                  height: "fit-content",
                }}
              >
                ACTIVE
              </span>
            )}
            {isFeatured && !isCurrent && (
              <span
                style={{
                  ...s.badge,
                  background: PRIMARY.W_100,
                  color: BRAND.PRIMARY,
                  border: `1px solid ${PRIMARY.W_200}`,
                  fontSize: "9px",
                  padding: "2px 8px",
                  height: "fit-content",
                }}
              >
                RECOMMENDED
              </span>
            )}
          </Box>
        </Box>

        {/* Price + Credits */}
        <Box
          style={{
            display: "grid",
            gap: "12px",
            background: BASE.BACKGROUND_LIGHT,
            borderRadius: "12px",
            border: `1px solid ${isFeatured ? PRIMARY.W_200 : BORDER.LIGHT}`,
            padding: "16px",
          }}
          sx={{
            gridTemplateColumns: {
              xs: "1fr",
              sm: "minmax(0, 1fr) auto",
            },
          }}
        >
          <Box style={{ display: "flex", flexDirection: "column", justifyContent: "space-between", minWidth: 0 }}>
            <TypographyInline
              size="xs"
              fontWeight={fontWeight.BOLD}
              color={GREY.TERTIARY}
              label="PRICE"
            />
            <Box style={{ display: "flex", alignItems: "baseline", gap: "4px", marginTop: "4px" }}>
              <TypographyInline
                size="dxs"
                fontWeight={fontWeight.BOLD}
                color={GREY.PRIMARY}
                label={`${plan.currency === "USD" ? "$" : plan.currency}${plan.price}`}
              />
              <TypographyInline
                size="sm"
                color={GREY.SECONDARY}
                fontWeight={fontWeight.MEDIUM}
                label={billingCycle === "month" ? "/mo" : "/yr"}
              />
            </Box>
            <Box style={{ marginTop: "2px" }}>
              <TypographyInline
                size="xs"
                color={GREY.TERTIARY}
                label={billingCycle === "year" ? "Billed annually" : "Billed monthly"}
              />
            </Box>
          </Box>

          <Box
            style={{
              minWidth: "120px",
              padding: "11px 12px",
              display: "flex",
              flexDirection: "column",
              justifyContent: "center",
              border: `1px solid ${BORDER.DEFAULT}`,
              borderRadius: "10px",
              background: BASE.WHITE,
            }}
            sx={{
              alignItems: {
                xs: "flex-start",
                sm: "flex-end",
              },
            }}
          >
            <TypographyInline
              size="xs"
              fontWeight={fontWeight.BOLD}
              color={GREY.TERTIARY}
              label="TOTAL CREDITS"
            />
            <Box style={{ marginTop: "1px", fontVariantNumeric: "tabular-nums" }}>
              <TypographyInline
                size="dxs"
                fontWeight={fontWeight.BOLD}
                color={BRAND.PRIMARY}
                label={plan.credits.toLocaleString()}
              />
            </Box>
            {billingCycle === "year" && (
              <Box style={{ marginTop: "2px", fontVariantNumeric: "tabular-nums" }}>
                <TypographyInline
                  size="xs"
                  color={GREY.TERTIARY}
                  label={`${Math.round(plan.credits / 12).toLocaleString()} / mo`}
                />
              </Box>
            )}
          </Box>
        </Box>

        {/* Feature table */}
        {Object.keys(featuresToRender).length > 0 && (
            <Box
              style={{
                overflow: "hidden",
                borderRadius: "10px",
                border: `1px solid ${BORDER.LIGHT}`,
                background: BASE.WHITE,
              }}
            >
              <Box
                style={{
                  display: "grid",
                  gridTemplateColumns: "minmax(0, 1fr) 70px 84px",
                  padding: "9px 12px",
                  alignItems: "center",
                  background: BORDER.DISABLED,
                  borderBottom: `1px solid ${BORDER.LIGHT}`,
                }}
              >
                <TypographyInline
                  size="xs"
                  fontWeight={fontWeight.BOLD}
                  color={GREY.TERTIARY}
                  label="FEATURE"
                />
                <Box style={{ textAlign: "right" }}>
                  <TypographyInline
                    size="xs"
                    fontWeight={fontWeight.BOLD}
                    color={GREY.TERTIARY}
                    label="COST"
                  />
                </Box>
                <Box style={{ textAlign: "right" }}>
                  <TypographyInline
                    size="xs"
                    fontWeight={fontWeight.BOLD}
                    color={GREY.TERTIARY}
                    label="LIMIT"
                  />
                </Box>
              </Box>
              <Box style={{ display: "flex", flexDirection: "column" }}>
                {Object.entries(featuresToRender).map(
                  ([key, value], idx, arr) => (
                    <Box
                      key={key}
                      style={{
                        display: "grid",
                        gridTemplateColumns: "minmax(0, 1fr) 70px 84px",
                        padding: "10px 12px",
                        alignItems: "center",
                        background: idx % 2 === 0 ? BASE.WHITE : BASE.BACKGROUND_LIGHT,
                        borderBottom:
                          idx < arr.length - 1
                            ? `1px solid ${BORDER.LIGHT}`
                            : "none",
                      }}
                    >
                      <Box style={{ display: "flex", alignItems: "center", gap: "8px" }}>
                        <Box
                          style={{
                            width: "6px",
                            height: "6px",
                            borderRadius: "50%",
                            background: BRAND.PRIMARY,
                            flexShrink: 0,
                          }}
                        />
                        <TypographyInline
                          size="xs"
                          fontWeight={fontWeight.MEDIUM}
                          color={GREY.SECONDARY}
                          label={FEATURE_KEY_LABELS[key] || key}
                        />
                      </Box>
                      <Box style={{ textAlign: "right", fontVariantNumeric: "tabular-nums" }}>
                        <TypographyInline
                          size="xs"
                          fontWeight={fontWeight.BOLD}
                          color={BRAND.PRIMARY}
                          label={value?.credits ?? "-"}
                        />
                      </Box>
                      <Box style={{ textAlign: "right", fontVariantNumeric: "tabular-nums" }}>
                        <TypographyInline
                          size="xs"
                          fontWeight={fontWeight.BOLD}
                          color={GREY.PRIMARY}
                          label={value?.upto ?? "-"}
                        />
                      </Box>
                    </Box>
                  ),
                )}
              </Box>
            </Box>
          )}

        {/* CTA */}
        <Box style={{ marginTop: "auto" }}>
          {isSelected ? (
            <Button
              label={
                buyingPlanId === plan.id
                  ? "Processing..."
                  : isCurrent
                    ? "Current Plan"
                    : plan.isFree && takenOnceFreePlan
                      ? "Locked"
                      : hasActiveSub && !plan.isTopUp
                        ? "Switch to this plan"
                        : "Buy Plan"
              }
              size="sm36"
              variant={isCurrent ? "ghost" : "primary"}
              fullWidth
              disabled={isCurrent || buyingPlanId !== null}
              onClick={(e) => {
                e.stopPropagation();
                if (!isCurrent) onBuy(plan);
              }}
            />
          ) : (
            <Box
              style={{
                textAlign: "center",
                minHeight: "38px",
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                borderRadius: "10px",
                border: `1px solid ${isFeatured ? PRIMARY.W_200 : BORDER.DEFAULT}`,
                background: isFeatured ? PRIMARY.W_50 : BASE.WHITE,
                opacity: isLocked ? 0.6 : 1,
                transition:
                  "border-color 0.18s ease, background 0.18s ease, transform 0.18s ease",
                cursor: "pointer",
              }}
              sx={{
                "&:hover": {
                  borderColor: BRAND.PRIMARY,
                  background: PRIMARY.W_50,
                  transform: "translateY(-1px)",
                },
              }}
            >
              <TypographyInline
                size="xs"
                fontWeight={fontWeight.SEMIBOLD}
                color={isFeatured ? BRAND.PRIMARY : GREY.SECONDARY}
                label={
                  isCurrent
                    ? "Current Plan"
                    : isLocked
                      ? "Locked"
                      : "Select Plan"
                }
              />
            </Box>
          )}
        </Box>
      </Box>
    );
  },
);

BillingPlanCard.displayName = "BillingPlanCard";

export default BillingPlanCard;
